// StaffListView.js
import React, { useState, useEffect, useRef } from 'react';
import { Table, Checkbox, Button, Input, message, Modal, Form, Select, Spin, List, Slider, Collapse, Tooltip, Popover, Alert, Menu, Dropdown, Tabs, Space } from 'antd';
import { Box } from '@mui/material';
import api from "../../../authentication/axiosInstance";
import { ArchiveWithAllocationsButton, UnarchiveButton } from '../../../components/allocation/ArchiveWithAllocationsModal';
import debounce from 'lodash/debounce';
import {EditOutlined, PlusOutlined, SearchOutlined, DownloadOutlined, DownOutlined } from '@ant-design/icons';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import {
  STAFF_POSITIONS, STAFF_POSITION_FILTERS, LOADER_POSITIONS, CURRENT_LOADER_POSITIONS, RENAMED_POSITIONS, editablePositions,
} from '../../../utils/staffPositions';
import FailedPositionNotificationsBanner from './FailedPositionNotificationsBanner';
import styles from './StaffListView.module.css';
import AssignmentList, { assignmentKey } from '../../../components/allocation/AssignmentList';


const { Option } = Select;
// The staff page's gold primary buttons; disabled buttons keep antd's disabled look.
const goldButtonStyle = (disabled) => (disabled ? undefined : { backgroundColor: '#cdaf5e', borderColor: '#cdaf5e' });

const StaffListView = () => {
  const [showModal, setShowModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [staff, setStaff] = useState([]);
  const [archiveTab, setArchiveTab] = useState('active');
  const [editingStaff, setEditingStaff] = useState(null);
  const [editForm] = Form.useForm();
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [positionBlockers, setPositionBlockers] = useState(null);
  const [blockersLoading, setBlockersLoading] = useState(false);
  const [blockersError, setBlockersError] = useState(false);
  const [staffDetailsLoading, setStaffDetailsLoading] = useState(false);
  const [staffDetailsError, setStaffDetailsError] = useState(false);
  const [removalPendingKey, setRemovalPendingKey] = useState(null);
  const [positionPopoverOpen, setPositionPopoverOpen] = useState(false);
  const [positionPopoverPinned, setPositionPopoverPinned] = useState(false);
  const editSession = useRef(0);
  const blockerRequest = useRef(0);
  const dirtyFields = useRef(new Set());
  const positionTriggerRef = useRef(null);
  const positionPopoverRef = useRef(null);
  const skipFocusOpen = useRef(false);
  const focusPopoverOnOpen = useRef(false);
  const removalPending = removalPendingKey !== null;
  useEffect(() => () => { editSession.current += 1; blockerRequest.current += 1; }, []);

  const applyPositionBlockers = (staffId, data) => {
    setPositionBlockers(data);
    if (editingStaff?.staff_id === staffId && data.current_position !== undefined) {
      // A pending rename to the role's new name is still allowed while allocated.
      const keepsRename = editingStaff.position === data.current_position
        && RENAMED_POSITIONS[data.current_position] === editForm.getFieldValue('position');
      if (!keepsRename && (data.blocked || editingStaff.position !== data.current_position)) {
        editForm.setFieldsValue({ position: data.current_position });
        dirtyFields.current.delete('position');
      }
      setEditingStaff(current => ({ ...current, position: data.current_position }));
    }
  };

  const refreshPositionBlockers = async (staffId, session = editSession.current) => {
    const requestId = ++blockerRequest.current;
    setBlockersLoading(true);
    setBlockersError(false);
    try {
      const response = await api.get(`/staff/${staffId}/position-change-blockers/`);
      if (session !== editSession.current || requestId !== blockerRequest.current) return;
      applyPositionBlockers(staffId, response.data);
    } catch (error) {
      if (session === editSession.current && requestId === blockerRequest.current) setBlockersError(true);
    } finally {
      if (session === editSession.current && requestId === blockerRequest.current) setBlockersLoading(false);
    }
  };

  // One removal at a time per editor; it takes effect immediately, independent of saving the form.
  const removePositionBlocker = async (row) => {
    if (removalPending || !editingStaff) return;
    const session = editSession.current;
    const staffId = editingStaff.staff_id;
    blockerRequest.current += 1; // An older allocation check must not overwrite this result.
    setBlockersLoading(false);
    setBlockersError(false);
    setRemovalPendingKey(assignmentKey(row));
    try {
      const { data } = await api.post(`/staff/${staffId}/position-change-blockers/remove/`, {
        ...row.remove_target, expected_assignment_hash: row.assignment_hash,
      });
      if (session !== editSession.current) return;
      applyPositionBlockers(staffId, data);
      message.success(data.blocked ? 'Allocation removed.' : 'Allocation removed. Position can now be changed.');
    } catch (error) {
      if (session !== editSession.current) return;
      const data = error.response?.data;
      if (data?.setups) applyPositionBlockers(staffId, data);
      else refreshPositionBlockers(staffId, session);
      message.error(data?.detail || 'Failed to remove the allocation.');
    } finally {
      if (session === editSession.current) setRemovalPendingKey(null);
    }
  };

  const closePositionPopover = () => {
    if (removalPending) return;
    setPositionPopoverPinned(false);
    setPositionPopoverOpen(false);
    // Return focus without the trigger's focus handler reopening the popover.
    skipFocusOpen.current = true;
    positionTriggerRef.current?.focus();
    skipFocusOpen.current = false;
  };

  // The popover renders outside the form, so keyboard users move into it explicitly.
  const handlePositionTriggerKey = (event) => {
    if (event.key === 'Escape' && positionPopoverOpen) {
      event.stopPropagation(); // Escape closes the popover, not the edit modal and its unsaved edits.
      closePositionPopover();
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (positionPopoverOpen && positionPopoverPinned) {
        focusFirstPopoverControl();
        return;
      }
      // Focus moves in after this state commits (see the effect below), so the trigger's blur
      // handler already sees the popover as pinned and doesn't close it.
      focusPopoverOnOpen.current = true;
      setPositionPopoverPinned(true);
      setPositionPopoverOpen(true);
    }
  };

  useEffect(() => {
    if (focusPopoverOnOpen.current && positionPopoverOpen && positionPopoverPinned && focusFirstPopoverControl()) {
      focusPopoverOnOpen.current = false;
    }
  });

  // Returns whether a control in the popover received focus.
  const focusFirstPopoverControl = () => {
    const control = positionPopoverRef.current?.querySelector('button:not([disabled])');
    control?.focus();
    return Boolean(control) && document.activeElement === control;
  };


  useEffect(() => {
    if (!positionPopoverPinned) return undefined;
    const closeOnOutsideClick = (event) => {
      if (positionTriggerRef.current?.contains(event.target) || positionPopoverRef.current?.contains(event.target)) return;
      if (removalPending) return;
      setPositionPopoverPinned(false);
      setPositionPopoverOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [positionPopoverPinned, removalPending]);


  const [isAddModalVisible, setIsAddModalVisible] = useState(false); //These are for the add staff modal
  const [addForm] = Form.useForm()

  const [candidates, setCandidates] = useState([]); // State to store candidates
  const [loadingCandidates, setLoadingCandidates] = useState(false); // Loading state for candidates
  const [selectedCandidates, setSelectedCandidates] = useState([]); // To track selected candidates

  const [ratingRange, setRatingRange] = useState([3, 5]); // Range for choosing candidate ratings

  const [positionFilter, setPositionFilter] = useState(null); //These are for filtering
  const [typeFilter, setTypeFilter] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [searchCityText, setSearchCityText] = useState('');
  const [ratingFilter, setRatingFilter] = useState(undefined);
  const [ratingOverallFilter, setRatingOverallFilter] = useState(undefined);

  const [staffType, setStaffType] = useState(null); //This is for conditionally rendering if type is subcontractor, to allow subcont. name

  const [loadingSync, setLoadingSync] = useState(false); // Staff Sync loading state
  const [notificationRefreshKey, setNotificationRefreshKey] = useState(0); // Reloads failed position emails after a sync
  const [loadingViaesysSync, setLoadingViaesysSync] = useState(false);

  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0
  });

  const fetchStaff = async (page = 1, pageSize = 20) => {
    setLoading(true)
    try {
      await api.get('/staff/staff_by_pages/', {
        params: {
          page,
          pageSize,
          position: positionFilter ? positionFilter : undefined,
          typeFilter: typeFilter ? typeFilter : undefined,
          searchText: searchText || undefined,
          searchCityText: searchCityText || undefined,
          ratingFilter: ratingFilter,
          ratingOverallFilter: ratingOverallFilter,
          archived: archiveTab === 'archived',
        }
      }).then((response) => {
        setStaff(response.data.data);
        setPagination({
          current: response.data.page,
          pageSize: response.data.pageSize,
          total: response.data.total,
          totalPages: response.data.totalPages
        });
      });
   
    } catch (error) {
      message.error('Failed to fetch staff');
    } finally {
      setLoading(false)
    }
  };

  const showEditModal = async (staff) => {
    const session = ++editSession.current;
    dirtyFields.current = new Set();
    editForm.resetFields();
    setEditingStaff(null);
    setPositionBlockers(null);
    setStaffDetailsLoading(true);
    setStaffDetailsError(false);
    setIsEditModalVisible(true);
    refreshPositionBlockers(staff.staff_id, session);
    try {
      const { data } = await api.get(`/staff/${staff.staff_id}/`);
      if (session !== editSession.current) return;
      setEditingStaff(data);
      setStaffType(data.type);
      editForm.setFieldsValue({ ...data, status: data.status || 'Unconfirmed' });
    } catch (error) {
      if (session === editSession.current) setStaffDetailsError(true);
    } finally {
      if (session === editSession.current) setStaffDetailsLoading(false);
    }
  };

  const handleEditCancel = () => {
    editSession.current += 1;
    blockerRequest.current += 1;
    setRemovalPendingKey(null);
    setPositionPopoverPinned(false);
    setPositionPopoverOpen(false);
    setIsEditModalVisible(false);
    setEditingStaff(null);
    editForm.resetFields();
  };

  // Allocated staff on an old role name may still take its new name, which keeps their permissions.
  const renameTarget = RENAMED_POSITIONS[editingStaff?.position];
  const renameOnly = Boolean(positionBlockers?.blocked && renameTarget);
  const positionDisabled = staffDetailsLoading || !editingStaff || blockersLoading || blockersError || !positionBlockers
    || (positionBlockers.blocked && !renameOnly) || removalPending;
  const editPositionOptions = renameOnly ? [editingStaff.position, renameTarget] : editablePositions(editingStaff?.position);
  const positionHelp = <div ref={positionPopoverRef}
    onMouseDown={() => setPositionPopoverPinned(true)}
    onKeyDown={(event) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation(); // The popover is portaled inside the modal's React tree.
      closePositionPopover();
    }}
    style={{ width: 420, maxWidth: '80vw', maxHeight: '60vh', overflowY: 'auto' }}>
    {blockersLoading ? <Spin size="small" /> : blockersError ?
      <Alert type="error" message="Unable to check allocations. Retry before changing position." /> :
      positionBlockers?.blocked ? <>
        <p>Deallocate this staff member from every assignment below before changing their position. Locked setups do not block position changes.</p>
        {renameOnly && <p>Renaming to {renameTarget} keeps these allocations and needs no deallocation.</p>}
        <AssignmentList setups={positionBlockers.setups} live={positionBlockers.live}
          canRemove={Boolean(positionBlockers.can_remove)} onRemove={removePositionBlocker}
          removalBusy={removalPending} pendingKey={removalPendingKey} />
      </> : <p>No blocking allocations found.</p>}
    <Space style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
      {blockersError && <Button size="small" type="primary" style={{ backgroundColor: '#cdaf5e', borderColor: '#cdaf5e' }}
        disabled={!editingStaff || blockersLoading} onClick={() => refreshPositionBlockers(editingStaff.staff_id)}>
        Retry
      </Button>}
      <Button size="small" type="primary" style={goldButtonStyle(removalPending)}
        disabled={removalPending} onClick={closePositionPopover}>Close details</Button>
    </Space>
  </div>;

  const handleModalCancel = () => {
    setShowModal(false);
    setSelectedStaff(null);
  };

  const handleEditSubmit = async (values) => {
    if (!editingStaff || staffDetailsLoading || removalPending) return;
    const session = editSession.current;
    const formattedValues = Object.fromEntries(Object.entries(values).filter(([key, value]) =>
      dirtyFields.current.has(key) && value !== editingStaff[key]));
    if ('position' in formattedValues) {
      if (positionDisabled || (renameOnly && formattedValues.position !== renameTarget)) return;
      formattedValues.expected_position = editingStaff.position;
    }
    try {
      await api.patch(`/staff/${editingStaff.staff_id}/`, formattedValues);
      if (session !== editSession.current) return;
      message.success('Staff updated successfully');
      handleEditCancel();
      fetchStaff(pagination.current, pagination.pageSize);
    } catch (error) {
      if (session !== editSession.current) return;
      if (error.response?.status === 409) {
        const conflict = error.response.data;
        if (conflict.current_position !== undefined) {
          setEditingStaff(current => ({ ...current, position: conflict.current_position }));
          editForm.setFieldsValue({ position: conflict.current_position });
          dirtyFields.current.delete('position');
          blockerRequest.current += 1;
          setBlockersLoading(false);
          setBlockersError(false);
          setPositionBlockers(conflict);
        } else {
          refreshPositionBlockers(editingStaff.staff_id, session);
        }
        message.error(conflict.detail || 'Staff allocations changed. Review and try again.');
      } else message.error('Failed to update staff');
    }
  };

  const showAddModal = () => {
    setIsAddModalVisible(true);
  }

  const handleAddModalCancel = () => {
    setIsAddModalVisible(false);
    addForm.resetFields();
    setStaffType(null)
  };


  const handleAddModalSubmit = async (values) => {
    try {
      const formattedValues = {
        ...values,
        assigned_bool:0,
        first_name: "n/a",
        last_name: "n/a",
        job_title: "n/a",
        pref_name: null,
        pay_rate : parseFloat(values.pay_rate),
        assigned_walkway_crew_bool: 0,
        assigned_salting_route_bool: 0,
      };
      setStaffType(null);
      const response = await api.post('/staff/', formattedValues);
      message.success('Staff added successfully');
      setIsAddModalVisible(false);
      addForm.resetFields();
      fetchStaff(pagination.current,pagination.pageSize);
    } catch (error) {
      console.error('Failed to add staff:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
        if (typeof error.response.data === 'object' && error.response.data !== null) {
          const errorMessages = Object.entries(error.response.data)
            .map(([key, value]) => `${key}: ${value}`)
            .join(', ');
          message.error(`Failed to add staff: ${errorMessages}`);
        } else {
          message.error('Failed to add staff. Please check your input and try again.');
        }
      } else if (error.request) {
        console.error('Error request:', error.request);
        message.error('Network error. Please check your connection and try again.');
      } else {
        message.error('An unexpected error occurred. Please try again.');
      }
    }
  };

  const handleStatusChange = async (staffId, newStatus) => {
    try {
      await api.patch(`/staff/${staffId}/`, { status: newStatus });
      message.success('Status updated successfully');
      fetchStaff(pagination.current,pagination.pageSize);
    } catch (error) {
      console.error('Failed to update status:', error);
      message.error('Failed to update status');
    }
  };

  const handleCheckboxChange = (staff, field, newValue) => {
    Modal.confirm({
      title: `Are you sure you want to make this change?`,
      onOk: async () => {
        try {
          const updatedData = { [field]: newValue };
          const response = await api.patch(`/staff/${staff.staff_id}/`, updatedData);
          console.log("API response:", response);
          console.log("Staff after update:", staff);
  
          if (response.status === 200 || response.status === 204) {
            message.success(`Field updated successfully`);
  
            setStaff((prevStaff) =>
              prevStaff.map((s) =>
                s.staff_id === staff.staff_id ? { ...s, [field]: newValue } : s
              )
            );
          } else {
            console.error('Unexpected response:', response);
            message.error(`Failed to update ${field.replace('_', ' ')}`);
          }
        } catch (error) {
          message.error(`Failed to update ${field.replace('_', ' ')}`);
          console.error('Update error:', error);
        }
      },
      onCancel() {
        message.info('No changes were made');
      },
    });
  };

  const columns = [ // adding a comment here, hopefully change is detected and code is picked up in prod.
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'Staff_Name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      width: 110,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'Staff_Type',
      width: 90,
      className: styles.wrappingTextCell,
    },
    {
      title: 'Position',
      dataIndex: 'position',
      key: 'Staff_Position',
      width: 110,
      className: styles.wrappingTextCell,
    },
    {
      title: "City",
      dataIndex: 'city',
      key: "Staff_City",
      width: 80,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status, record) => (
        <Select
          value={status || 'Unconfirmed'}
          size="small"
          className={styles.statusSelect}
          popupClassName={styles.statusDropdown}
          style={{ width: 128 }}
          onChange={(value) => handleStatusChange(record.staff_id, value)}
        >
          <Option value="Unconfirmed">Unconfirmed</Option>
          <Option value="Confirmed">Confirmed</Option>
        </Select>
      ),
    },
    {
      title: 'Staff Rating',
      dataIndex: 'staff_rating',
      key: 'Staff_Rating',
      width: 85
    },
    {
      title: 'Overall Rating',
      dataIndex: 'staff_overall_rating',
      key: 'staff_overall_rating',
      width: 60
    },
    {
      title: "Part Time",
      dataIndex: 'is_part_time',
      key: "is_part_time",
      width: 52,
      render: (isPartTime, record) => (
        <Checkbox
          checked={isPartTime}
          onChange={() => handleCheckboxChange(record, 'is_part_time', !isPartTime)}
        />
      ),
    },
    {
      title: "Snow Edge",
      dataIndex: 'is_snow_edge',
      key: "is_snow_edge",
      width: 52,
      render: (isSnowEdge, record) => (
        <Tooltip
        title={LOADER_POSITIONS.includes(record.position) ? "" : `Available for ${CURRENT_LOADER_POSITIONS.join(', ')} staff`}
        overlayStyle={{ fontSize: '12px', padding: '4px 8px' }} // Adjust font size and padding
        >          
        <Checkbox
            checked={isSnowEdge}
            onChange={() => handleCheckboxChange(record, 'is_snow_edge', !isSnowEdge)}
            disabled={!LOADER_POSITIONS.includes(record.position)} // Available to loader-eligible positions
          />
      </Tooltip>
      ),
    },
    {
      title: "Back Up",
      dataIndex: 'is_back_up',
      key: "is_back_up",
      width: 52,
      render: (isBackUp, record) => (
        <Checkbox
          checked={isBackUp}
          onChange={() => handleCheckboxChange(record, 'is_back_up', !isBackUp)}
        />
      ),
    },
    {
      title: "TM",
      dataIndex: 'is_territory_manager',
      key: "is_territory_manager",
      width: 40,
      render: (isTerritoryManager, record) => (
        <Checkbox
          checked={isTerritoryManager}
          onChange={() => handleCheckboxChange(record, 'is_territory_manager', !isTerritoryManager)}
        />
      ),
    },
    {
      title: "Dispatcher",
      dataIndex: 'is_dispatcher',
      key: "is_dispatcher",
      width: 60,
      render: (isDispatcher, record) => (
        <Checkbox
          checked={isDispatcher}
          onChange={() => handleCheckboxChange(record, 'is_dispatcher', !isDispatcher)}
        />
      ),
    },
    {
      title: 'Mobile Login Verified',
      dataIndex: 'is_mobile_login_verified',
      key: 'is_mobile_login_verified',
      width: 145,
      sorter: (a, b) => Number(Boolean(a.is_mobile_login_verified)) - Number(Boolean(b.is_mobile_login_verified)),
      render: (verified, record) => (
        <span title={record.mobile_login_verified_at
          ? `First successful mobile login: ${new Date(record.mobile_login_verified_at).toLocaleString()}`
          : 'No successful linked field-staff login recorded yet. This is separate from push delivery.'}>
          <Checkbox checked={Boolean(verified)} disabled />
        </span>
      ),
    },
    {
      title: 'Phone Verified',
      dataIndex: 'is_phone_verified',
      key: 'is_phone_verified',
      width: 110,
      sorter: (a, b) => a.is_phone_verified - b.is_phone_verified,
      render: (isPhoneVerified, record) => (
        <>
          <Checkbox checked={isPhoneVerified} disabled />
          {!isPhoneVerified && (
            <Button
              type="Primary"
              className={styles.verifyButton}
              style={{ backgroundColor: '#cdaf5e', borderColor: '#cdaf5e', marginLeft: 8, color: 'white' }}
              onClick={() => sendVerificationMessage(record.staff_id)}
            >
              Verify
            </Button>
          )}
        </>
      ),
    },
    {
      title: "Photo",
      dataIndex: 'have_photo',
      key: "have_photo",
      width: 42,
      render: (havePhoto, record) => (
        <Checkbox
          checked={havePhoto}
          onChange={() => handleCheckboxChange(record, 'have_photo', !havePhoto)}
        />
      ),
    },
    {
      title: "In Fleetio",
      dataIndex: 'is_in_fleetio',
      key: "is_in_fleetio",
      width: 50,
      render: (isInFleetio, record) => (
        <Checkbox
          checked={isInFleetio}
          onChange={() => handleCheckboxChange(record, 'is_in_fleetio', !isInFleetio)}
        />
      ),
    },
    {
      title: "Language",
      dataIndex: 'language',
      key: "language",
      width: 65,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 108,
      render: (staff) => (
        <div className={styles.actions}>
          {archiveTab === 'active' ? (
            <>
              <Button
                onClick={() => showEditModal(staff)}
                type="primary"
                icon={<EditOutlined />}
                style={{ backgroundColor: '#cdaf5e', borderColor: '#cdaf5e' }}
              >
                Edit
              </Button>
              <ArchiveWithAllocationsButton
                itemType="staff"
                itemId={staff.staff_id}
                itemLabel={staff.name}
                onArchived={() => fetchStaff(pagination.current, pagination.pageSize)}
                buttonProps={{ className: 'delete-button' }}
              />
            </>
          ) : (
            <UnarchiveButton
              itemType="staff"
              itemId={staff.staff_id}
              itemLabel={staff.name}
              onUnarchived={() => fetchStaff(pagination.current, pagination.pageSize)}
            />
          )}
        </div>
      ),
    },
    ...(archiveTab === 'archived' ? [
      {
        title: 'Archived At',
        dataIndex: 'archived_at',
        key: 'archived_at',
        width: 120,
        render: (value) => value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '—',
      },
    ] : []),
  ];

  // Fetch candidates from BambooHR API with selected rating range
  const fetchCandidates = async () => {
    setLoadingCandidates(true);
    try {
      const response = await api.get('/bambooHR/rated_candidates/', {
        params: {
          min_rating: ratingRange[0],
          max_rating: ratingRange[1],
        },
      });
      setCandidates(response.data.candidates); // Store the candidates in state
      message.success('Candidates fetched successfully!');
    } catch (error) {
      message.error('Failed to fetch candidates.');
    } finally {
      setLoadingCandidates(false);
    }
  };

  // Handle candidate selection
  const handleCandidateSelection = (candidateId) => {
    setSelectedCandidates((prevSelected) => {
      if (prevSelected.includes(candidateId)) {
        return prevSelected.filter(id => id !== candidateId); // Deselect candidate
      } else {
        return [...prevSelected, candidateId]; // Select candidate
      }
    });
  };

  const handleConfirmCandidates = async () => {
    try {
      // Filter out the selected candidates
      const selectedCandidateData = candidates.filter(candidate => selectedCandidates.includes(candidate.id));

      // Send the selected candidate data to the backend
      const response = await api.post('/staff/convert-candidates/', {
        candidates: selectedCandidateData,  
      });
      message.success('Candidates successfully converted to staff!');
      setSelectedCandidates([]);  // Reset selected candidates
      fetchStaff(pagination.current,pagination.pageSize);
    } catch (error) {
      console.error('Error converting candidates:', error);
      message.error('Failed to convert candidates.');
    }
  };

  //Filtering and searching
  // const applyFilters = () => {
  //   let filtered = staff;
  //   if (searchText) {
  //     filtered = filtered.filter((staff) => 
  //       staff.name?.toLowerCase().includes(searchText.toLowerCase())
  //     );
  //   }
  //   if (searchCityText) {
  //     filtered = filtered.filter((staff) => 
  //       staff.city?.toLowerCase().includes(searchCityText.toLowerCase())
  //     );
  //   }
  //   if (positionFilter) {
  //     filtered = filtered.filter((staff) => staff.position === positionFilter);
  //   }
  //   if (typeFilter) {
  //     filtered = filtered.filter((staff) => staff.type === typeFilter);
  //   }

  //   if (ratingOverallFilter !== undefined) {
  //     if (ratingOverallFilter === null) {
  //       filtered = filtered.filter((staff) => !staff.staff_overall_rating || staff.staff_overall_rating === '');
  //     } else if (ratingOverallFilter === "Rated") {
  //       filtered = filtered.filter((staff) => staff.staff_overall_rating && staff.staff_overall_rating !== '');
  //     } else {
  //       filtered = filtered.filter((staff) => staff.staff_overall_rating === ratingOverallFilter);
  //     }
  //   }

  // if (ratingFilter !== undefined) {
  //   if (ratingFilter === null) {
  //     // Show only staff with no rating (null or undefined)
  //     filtered = filtered.filter((staff) => !staff.staff_rating);
  //   } else if (ratingFilter === "Rated") {
  //     // Show only staff with a rating (non-null)
  //     filtered = filtered.filter((staff) => staff.staff_rating);
  //   } else {
  //     // Show staff with the selected rating
  //     filtered = filtered.filter((staff) => staff.staff_rating === ratingFilter);
  //   }
  // }
  
  //   setFilteredStaff(filtered);
  // };


  const debouncedSearch = debounce((value) => {
    setSearchText(value);
  }, 300);

  const debouncedCitySearch = debounce((value) => {
    setSearchCityText(value);
  }, 300);


  useEffect(() => {
    fetchStaff(1, pagination.pageSize)
  }, [positionFilter, typeFilter, searchText, searchCityText, ratingFilter, ratingOverallFilter, archiveTab]);

  const handleStaffTypeChange = (value) => {
    setStaffType(value);
  };

  const fields = [
    'firstName',
    'lastName',
    'preferredName',
    'workPhone',
  ]

  // Function to sync staff members from BambooHR
  const syncStaffMembers = async () => {
    setLoadingSync(true);
    try {
      const response = await api.post('/bambooHR/config/', fields, { responseType: 'blob' });
      // const response = await api.post('/bambooHR/sync-employee-data/', fields, { responseType: 'blob' });
      const contentType = response.headers['content-type'];
      if (contentType === 'application/pdf') {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        saveAs(blob, 'BambooSync_Details.pdf');
        message.success('Staff sync completed successfully!');
        fetchStaff(); //reset pagination here
        setNotificationRefreshKey((key) => key + 1);
      } else if (contentType === 'application/json') {
        const errorText = await response.data.text();
        const errorJson = JSON.parse(errorText);
        message.error(`Failed to sync staff: ${errorJson.message || 'Unknown error'}`);
      } else {
        message.error('Failed to sync staff: Unexpected response format.');
      }
    } catch (error) {
      console.error('Failed to sync staff:', error);
        message.error('Failed to sync staff.');
      } finally {
        setLoadingSync(false);
      }
    };

  // Function to sync staff members from Viaesys
  const syncViaesysStaff = async () => {
    setLoadingViaesysSync(true);
    try {
      const response = await api.post('/viaesys/user-sync/', {}, { responseType: 'blob' });
      const contentType = response.headers['content-type'];

      if (contentType === 'application/pdf') {
        // If the response is a PDF, create a blob and download it
        const blob = new Blob([response.data], { type: 'application/pdf' });
        saveAs(blob, 'ViaesysSync_Details.pdf');
        message.success('Viaesys staff sync completed successfully!');
        fetchStaff(); //reset pagination here
      } else if (contentType === 'application/json') {
        // If the response is JSON, parse it and display an error message
        const errorText = await response.data.text();
        const errorJson = JSON.parse(errorText);
        message.error(`Failed to sync staff: ${errorJson.message || 'Unknown error'}`);
      } else {
        message.error('Failed to sync staff: Unexpected response format.');
      }
    } catch (error) {
      console.error('Failed to sync staff from Viaesys:', error);
      message.error('Failed to sync staff from Viaesys.');
    } finally {
      setLoadingViaesysSync(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await api.get('/staff/export_csv/', {
        responseType: 'blob'  
      });
      
      // create a download link and trigger it
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'staff_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      message.error('Failed to export staff data');
      console.error('Export error:', error);
    }
  };

  const verifyStaffNumbers = async () => {
    try {
      const response = await api.post('/staff/send_verification_messages/');
      if (response.status === 201) {
        message.success('Verification messages sent successfully');
      } else {
        message.error('Failed to send verification messages');
      }
    } catch (error) {
      message.error('An error occurred while sending verification messages');
      console.error('Error details:', error);
    }
  };

  const sendVerificationMessage = async (staffId) => {
    try {
      const response = await api.post('/staff/send_individual_verification/', { staff_id: staffId });
      if (response.status === 201) {
        message.success('Verification message sent successfully');
      } else {
        message.error('Failed to send verification message');
      }
    } catch (error) {
      message.error('An error occurred while sending the verification message');
      console.error('Error details:', error);
    }
  };

  const syncMenu = (
    <Menu>
      <Menu.Item key="syncStaff" onClick = {syncStaffMembers}>
          {loadingSync ? (
            <Spin />
          ) : (
            <span>
              Sync Staff Members Bamboo
              <img src="/BambooHR logo black.png" alt="BambooHR" style={{ width: '15px', marginLeft: '8px' }} />
            </span>
          )}
      </Menu.Item>
      <Menu.Item key="syncViaesys" onClick = {syncViaesysStaff}>
          {loadingViaesysSync ? (
            <Spin />
          ) : (
            <span>
              Sync Staff Members Viaesys
              <img src="/Viaesys logo.png" alt="Viaesys" style={{ width: '15px', marginLeft: '8px' }} />
            </span>
          )}
      </Menu.Item>
    </Menu>
  );

  return (
    <Box className={styles.page}>
      <h3 className={styles.title}>
        Staff:
      </h3>
      <FailedPositionNotificationsBanner refreshKey={notificationRefreshKey} />
      {/* Syncing overlay */}
      <Modal
        visible={loadingSync}
        title={null}
        footer={null}
        centered
        closable={false}
        maskClosable={false}
        bodyStyle={{ textAlign: 'center', padding: 40 }}
      >
        <Spin size="large" style={{ marginBottom: 20 }} />
        <h2>Syncing BambooHR Staff</h2>
        <p>Please wait while we update your information...</p>
      </Modal>
      <Modal
        visible={loadingViaesysSync}
        title={null}
        footer={null}
        centered
        closable={false}
        maskClosable={false}
        bodyStyle={{ textAlign: 'center', padding: 40 }}
      >
        <Spin size="large" style={{ marginBottom: 20 }} />
        <h2>Syncing Viaesys Staff</h2>
        <p>Please wait while we update your information...</p>
      </Modal>
        <div className={styles.toolbarViewport}>
          <div className={styles.toolbar}>
            <div className={styles.actionGroup}>
              <Button
                onClick={showAddModal}
                type="primary"
                icon={<PlusOutlined />}
                size="small"
                className={styles.toolbarButton}
                style={{ backgroundColor: '#cdaf5e', borderColor: '#cdaf5e' }}
              >
                Add New Staff
              </Button>
              <Dropdown overlay={syncMenu} placement="bottomLeft">
                <Button
                  type="primary"
                  size="small"
                  className={styles.toolbarButton}
                  style={{ backgroundColor: '#cdaf5e', borderColor: '#cdaf5e' }}
                >
                  Sync Staff <DownOutlined />
                </Button>
              </Dropdown>
              <Button
                onClick={handleExportCSV}
                type="primary"
                icon={<DownloadOutlined />}
                size="small"
                className={styles.toolbarButton}
                style={{ backgroundColor: '#cdaf5e', borderColor: '#cdaf5e' }}
              >
                Export CSV
              </Button>
              <Button
                onClick={verifyStaffNumbers}
                type="primary"
                size="small"
                className={styles.toolbarButton}
                style={{ backgroundColor: '#cdaf5e', borderColor: '#cdaf5e' }}
              >
                Verify Staff Phone Numbers
              </Button>
            </div>
            <div className={styles.filterGroup}>
            <Input.Search
              placeholder="Search staff"
              onSearch={setSearchText}
              onChange={(e) => debouncedSearch(e.target.value)}
              size="small"
              className={styles.searchControl}
              prefix={<SearchOutlined />}
            />
            <Input.Search
              placeholder="Search city"
              onSearch={setSearchCityText}
              onChange={(e) => debouncedCitySearch(e.target.value)}
              size="small"
              className={styles.searchControl}
              prefix={<SearchOutlined />}
            />
            <Select
              placeholder="Position"
              size="small"
              className={styles.positionFilter}
              onChange={setPositionFilter}
              value={positionFilter}
              allowClear
            >
              {STAFF_POSITION_FILTERS.map(position => (
                <Option key={position} value={position}>{position}</Option>
              ))}
              <Option value={"n/a"}>Not Specified</Option>

            </Select>
            <Select
              placeholder="Staff type"
              size="small"
              className={styles.typeFilter}
              onChange={setTypeFilter}
              value={typeFilter}
              allowClear
            >
              <Option value={"Staff"}>Staff</Option>
              <Option value={"Candidate"}>Candidate</Option>
              <Option value={"Subcontractor"}>Subcontractor</Option>
            </Select>
            <Select
              placeholder="Staff Rating"
              size="small"
              className={styles.ratingFilter}
              onChange={setRatingFilter}
              value={ratingFilter}
              allowClear
            >
              <Option value="1A - Green">1A - Green</Option>
              <Option value="1B - Green">1B - Green</Option>
              <Option value="1C - Blue">1C - Blue</Option>
              <Option value="2 - Yellow">2 - Yellow</Option>
              <Option value="3 - Orange">3 - Orange</Option>
              <Option value="No">No</Option>
              <Option value="Rated">Rated</Option>
              <Option value={null}>Unspecified</Option>
            </Select>
            <Select
              placeholder="Overall Rating"
              size="small"
              className={styles.overallFilter}
              onChange={(value) => setRatingOverallFilter(value)}  // New state for overall rating filter
              value={ratingOverallFilter}
              allowClear  // Allow clearing the selection
            >
              <Option value="1">1</Option>
              <Option value="2">2</Option>
              <Option value="3">3</Option>
              <Option value="4">4</Option>
              <Option value="5">5</Option>
              <Option value="Rated">Rated</Option>
              <Option value={null}>Unspecified</Option>
            </Select>
            </div>
          </div>
        </div>
      <Tabs
        activeKey={archiveTab}
        onChange={setArchiveTab}
        style={{ marginBottom: 12 }}
        items={[
          { key: 'active', label: 'Active' },
          { key: 'archived', label: 'Archived' },
        ]}
      />
      <Spin spinning={loading}>
        <Table 
          dataSource={staff}
          columns={columns}
          rowKey="staff_id"
          size="small"
          className={styles.compactTable}
          defaultPageSize={20}
          pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              onChange: (page, pageSize) => {
                fetchStaff(page, pageSize);
              },
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            }}
          scroll={{ x: archiveTab === 'archived' ? 1426 : 1306, y: 640 }}  />
        </Spin>
      <Modal
              title="Staff Details"
              open={showModal}
              onCancel={handleModalCancel}
              footer={null}
            >
              {selectedStaff && (
                <div>
                  <p>Name: {selectedStaff.Staff_Name}</p>
                </div>
              )}
      </Modal>

      <Modal
        title="Add New Staff"
        visible={isAddModalVisible}
        onCancel={handleAddModalCancel}
        footer={null}
        width={800} // Increased width for two-column layout
      >
        <Form form={addForm} onFinish={handleAddModalSubmit} layout="vertical">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
            <Form.Item name="name" label="Staff name" rules={[{ required: true, message: 'Please enter the staff name' }]} style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Input />
            </Form.Item>
            <Form.Item name="position" label="Position" rules={[{ required: true, message: 'Please select the position' }]} style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Select placeholder="Select position">
                {STAFF_POSITIONS.map(position => (
                <Option key={position} value={position}>{position}</Option>
              ))}
                <Option value="n/a">Not Specified</Option>
              </Select>
            </Form.Item>
            <Form.Item name="contract_type" label="Contract Type" rules={[{ required: false, message: 'Please select the contract type' }]} style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Select placeholder="Select contract type">
                <Option value="Hourly">Hourly</Option>
                <Option value="Salary">Salary</Option>
                <Option value="Standby">Standby</Option>
                <Option value="n/a">Not Specified</Option>
              </Select>
            </Form.Item>
            <Form.Item name="city" label="City" rules={[{ required: false, message: 'Please enter the city' }]} style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Input /> 
            </Form.Item>
            <Form.Item
              name="type"
              label="Staff Type"
              rules={[{ required: true, message: 'Please select the staff type' }]}
              style={{ flex: '1 1 calc(50% - 8px)' }}
            >
              <Select placeholder="Select staff type" onChange={handleStaffTypeChange} >
                <Option value="Staff">Staff</Option>
                <Option value="Candidate">Candidate</Option>
                <Option value="Subcontractor">Subcontractor</Option>
              </Select>
            </Form.Item>
            {/* Conditionally render the subcontractor name input */}
            {staffType === 'Subcontractor' && (
            <Form.Item
              name="subcontractor_name"
              label="Subcontractor Name"
              rules={[{ required: true, message: 'Please enter the subcontractor name' }]}
              style={{ flex: '1 1 calc(50% - 8px)' }}
            >
              <Input placeholder="Enter subcontractor name" />
          </Form.Item>
        )}
            <Form.Item name="pay_rate" label="Pay Rate" rules={[{ required: false, message: 'Please enter the pay rate' }]} style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Input /> 
            </Form.Item>
            <Form.Item name="work_phone_extension" label="Work Number Extension" rules={[{ required: false, message: 'Please enter the phone extension' }]} style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Input /> 
            </Form.Item>
            <Form.Item name="work_phone" label="Work Number" rules={[{ required: false, message: 'Please enter the phone number' }]} style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Input /> 
            </Form.Item>
            <Form.Item name="work_email" label="Email" rules={[{ required: true, message: 'Please enter the email address' }]} style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Input /> 
            </Form.Item>
            <Form.Item name="staff_rating" label="Staff Rating" rules={[{ required: false, message: 'Please select staff rating' }]} style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Select placeholder="Select staff rating">
                <Option value="1A - Green">1A - Green</Option>
                <Option value="1B - Green">1B - Green</Option>
                <Option value="1C - Blue">1C - Blue</Option>
                <Option value="2 - Yellow">2 - Yellow</Option>
                <Option value="3 - Orange">3 - Orange</Option>
                <Option value="No">No</Option>
                <Option value={null}>Unspecified</Option>
              </Select>
            </Form.Item>
            <Form.Item name="language" label="Language" rules={[{ required: false, message: 'Please select staff language' }]} style={{ flex: '1 1 calc(50% - 8px)' }}>
            <Select placeholder="Select staff language">
              <Option value="English">English</Option>
              <Option value="Spanish">Spanish</Option>
            </Select>
          </Form.Item>
            <Form.Item name="staff_overall_rating" label="Staff Overall Rating" rules={[{ required: false, message: 'Please select staff overall rating' }]} style={{ flex: '1 1 calc(50% - 8px)' }}>
            <Select placeholder="Select staff rating">
              <Option value="1">1</Option>
              <Option value="2">2</Option>
              <Option value="3">3</Option>
              <Option value="4">4</Option>
              <Option value="5">5</Option>
              <Option value={null}>Unspecified</Option>
            </Select>
          </Form.Item>
          </div>
            <Form.Item>
            <Button type="primary" htmlType="submit" style={{ backgroundColor: '#cdaf5e', borderColor: '#cdaf5e' }}>
              Add Staff
            </Button>
          </Form.Item>
        </Form>
      </Modal>
      {/* Edit Staff Modal */}
      <Modal
        title="Edit Staff"
        visible={isEditModalVisible}
        onCancel={handleEditCancel}
        footer={null}
        width={800}
      >
        <Form form={editForm}
            disabled={staffDetailsLoading || staffDetailsError}
            onValuesChange={(changed) => Object.keys(changed).forEach(key => dirtyFields.current.add(key))}
            onFinish={handleEditSubmit} layout="vertical">
            {staffDetailsLoading && <Spin aria-label="Loading staff details" />}
            {staffDetailsError && <Alert type="error" message="Unable to load staff details. Close and reopen to retry." />}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
            <Form.Item name="name" label="Staff Name" rules={[{ required: true, message: 'Please enter the staff name' }]} style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Input />
            </Form.Item>
            <Form.Item name="city" label="Staff city" rules={[{ required: false, message: 'Please enter the staff city' }]} style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Input />
            </Form.Item>
            <Form.Item name="work_phone" label="Staff work phone" rules={[{ required: false, message: 'Please enter the staff work phone' }]} style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Input />
            </Form.Item>
            <Form.Item name="work_phone_extension" label="Staff work phone extension" rules={[{ required: false, message: 'Please enter the staff work phone extension' }]} style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Input />
            </Form.Item>
            <Form.Item name="work_email" label="Staff work email" rules={[{ required: true, message: 'Please enter the staff work email' }]} style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Input />
            </Form.Item>
            {/* forceRender keeps the content mounted, so keyboard focus never waits for it to render */}
            <Popover content={positionHelp} trigger={['hover', 'click']} open={positionPopoverOpen} forceRender
              afterOpenChange={(open) => {
                if (open && focusPopoverOnOpen.current) focusFirstPopoverControl();
                focusPopoverOnOpen.current = false;
              }}
              onOpenChange={(next) => {
                // Once clicked or in use, only Escape, Close details or an outside click closes it.
                if (next) setPositionPopoverOpen(true);
                else if (!positionPopoverPinned && !removalPending) setPositionPopoverOpen(false);
              }}>
              <div tabIndex={0} aria-label="Position allocation information" ref={positionTriggerRef}
                onClick={() => setPositionPopoverPinned(true)}
                onFocus={() => { if (!skipFocusOpen.current) setPositionPopoverOpen(true); }}
                onBlur={() => { if (!positionPopoverPinned && !removalPending) setPositionPopoverOpen(false); }}
                onKeyDown={handlePositionTriggerKey}
                style={{ flex: '1 1 calc(50% - 8px)' }}>
            <Form.Item name="position" label="Position" rules={[{ required: true, message: 'Please select the position' }]} style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Select disabled={positionDisabled} placeholder="Select position">
                {editPositionOptions.map(position => (
                <Option key={position} value={position}>{position}</Option>
              ))}
                {!renameOnly && <Option value="n/a">Not Specified</Option>}
              </Select>
            </Form.Item>
              </div>
            </Popover>
            <Form.Item name="contract_type" label="Contract Type" rules={[{ required: false, message: 'Please select the contract type' }]} style={{ flex: '1 1 calc(50% - 8px)' }}>
              <Select placeholder="Select contract type">
                <Option value="Hourly">Hourly</Option>
                <Option value="Salary">Salary</Option>
                <Option value="Standby">Standby</Option>
                <Option value="n/a">Not Specified</Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="type"
              label="Staff Type"
              rules={[{ required: true, message: 'Please select the staff type' }]}
              style={{ flex: '1 1 calc(50% - 8px)' }}
            >
              <Select placeholder="Select staff type" 
              onChange={handleStaffTypeChange} 
              // disabled = {editingStaff?.type === "Staff"}
              // title={editingStaff?.type === "Staff" ? "Staff type is fixed for Staff" : ""}  // Custom hover message
              >
                <Option value="Staff">Staff</Option>
                <Option value="Candidate">Candidate</Option>
                <Option value="Subcontractor">Subcontractor</Option>
              </Select>
            </Form.Item>
            {/* Conditionally render the subcontractor name input */}
            {(staffType === 'Subcontractor' || editingStaff?.type === 'Subcontractor') && (
            <Form.Item
              name="subcontractor_name"
              label="Subcontractor Name"
              rules={[{ required: true, message: 'Please enter the subcontractor name' }]}
              style={{ flex: '1 1 calc(50% - 8px)' }}
            >
              <Input placeholder="Enter subcontractor name" />
            </Form.Item>
          )}
          <Form.Item
            name="status"
            label="Staff Status"
            rules={[{ required: true, message: 'Please select the staff status' }]}
            style={{ flex: '1 1 calc(50% - 8px)' }}
          >
            <Select placeholder="Select staff status">
              <Option value="Unconfirmed">Unconfirmed</Option>
              <Option value="Confirmed">Confirmed</Option>
            </Select>
          </Form.Item>
          <Form.Item name="staff_rating" label="Staff Rating" rules={[{ required: false, message: 'Please select staff rating' }]} style={{ flex: '1 1 calc(50% - 8px)' }}>
            <Select placeholder="Select staff rating">
              <Option value="1A - Green">1A - Green</Option>
              <Option value="1B - Green">1B - Green</Option>
              <Option value="1C - Blue">1C - Blue</Option>
              <Option value="2 - Yellow">2 - Yellow</Option>
              <Option value="3 - Orange">3 - Orange</Option>
              <Option value="No">No</Option>
              <Option value={null}>Unspecified</Option>
            </Select>
          </Form.Item>
          <Form.Item name="staff_overall_rating" label="Staff Overall Rating" rules={[{ required: false, message: 'Please select staff overall rating' }]} style={{ flex: '1 1 calc(50% - 8px)' }}>
            <Select placeholder="Select staff rating">
              <Option value="1">1</Option>
              <Option value="2">2</Option>
              <Option value="3">3</Option>
              <Option value="4">4</Option>
              <Option value="5">5</Option>
              <Option value={null}>Unspecified</Option>
            </Select>
          </Form.Item>
          <Form.Item name="language" label="Language" rules={[{ required: false, message: 'Please select staff language' }]} style={{ flex: '1 1 calc(50% - 8px)' }}>
            <Select placeholder="Select staff language">
              <Option value="English">English</Option>
              <Option value="Spanish">Spanish</Option>
            </Select>
          </Form.Item>
          </div>
          <Form.Item>
            <Button type="primary" htmlType="submit" disabled={removalPending} style={goldButtonStyle(removalPending)}>
              Update Staff
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Candidates Section */}
      <h3 style={{ paddingTop: 10, paddingBottom: 10 }}>Candidates:</h3>
      <Collapse>
        <Collapse.Panel header="Import and Select Candidates" key="1">
          <h4>Select Rating Range</h4>
          <Slider
            range
            defaultValue={ratingRange}
            min={1}
            max={5}
            onChange={(value) => setRatingRange(value)}
            style={{ width: 200 }}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: 200,
              marginBottom: 10,
              marginLeft: 5,
              fontWeight: "bold"
            }}
          >
            <span>1</span>
            <span>2</span>
            <span>3</span>
            <span>4</span>
            <span>5</span>
          </div>
          <Button
            onClick={fetchCandidates}
            type="primary"
            style={{ backgroundColor: '#cdaf5e', borderColor: '#cdaf5e', marginBottom: 20 }}
          >
            {loadingCandidates ? <Spin /> : 'Import Selected Rating Candidates'}
          </Button>

          {/* List of candidates with checkboxes for selection */}
          <List
            bordered
            dataSource={candidates}
            renderItem={(candidate) => (
              <List.Item>
                <Checkbox
                  checked={selectedCandidates.includes(candidate.id)}
                  onChange={() => handleCandidateSelection(candidate.id)}
                >
                  {candidate.applicant.firstName} {candidate.applicant.lastName} - Position: {candidate.job.title.label} - Rating: {candidate.rating}
                </Checkbox>
              </List.Item>
            )}
            style={{ maxHeight: 400, overflowY: 'auto' }}
          />

          {/* Confirm Candidate Selection Button */}
          <Button
            type="primary"
            onClick={handleConfirmCandidates}
            disabled={selectedCandidates.length === 0}
            style={{ backgroundColor: '#cdaf5e', borderColor: '#cdaf5e', marginTop: 20 }}
          >
            Confirm Selected Candidates as Staff
          </Button>
        </Collapse.Panel>
      </Collapse>
    </Box>
  );
};

export default StaffListView;
